import UnreadChatBadge from '../UnreadChatBadge';
import { useState } from "react";
import { Box, Button, Chip, Sheet, Stack, Typography } from "@mui/joy";
import { AnimatePresence, motion } from "framer-motion";

import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import ChatBubbleRoundedIcon from '@mui/icons-material/ChatBubbleRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import BuildRoundedIcon from '@mui/icons-material/BuildRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

import StarPicker from "./StarPicker";
import { FileStrip } from "./ReviewHistory";
import { fmtDateTime } from "./qualityTheme";

/**
 * Плашка контроля на карточке чертёжника.
 *
 * Три состояния, три разных сообщения:
 *   pending         — сдал, ждёт проверки. Ничего делать не надо, но и
 *                     «Завершить» второй раз жать незачем.
 *   rework          — вернули. Карточка пульсирует, показываем замечания
 *                     и кнопку «Принять на доработку».
 *   rework_accepted — взял в работу, кнопка события стала «Завершить доработку».
 *
 * props:
 *   quality  — блок quality из события
 *   onAccept — () => Promise  (принять доработку)
 *   onChat   — (chatId) => void
 */
export default function DraftsmanReworkPanel({ quality, onAccept, onChat }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(true);

  const state = quality?.state || '';
  if (!['pending', 'rework', 'rework_accepted'].includes(state)) return null;

  const last = quality?.last_review;
  const chatId = quality?.chat_id;

  if (state === 'pending') {
    return (
      <Sheet variant="soft" sx={{
        p: 1.25, borderRadius: '12px', mb: 1.25,
        bgcolor: '#f3f0ff', border: '1px solid #ddd6fe',
      }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <CheckCircleRoundedIcon sx={{ fontSize: 18, color: '#7b5cff' }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography level="body-sm" sx={{ fontWeight: 700, color: '#4c1d95' }}>
              Сдано на контроль качества
            </Typography>
            <Typography level="body-xs" sx={{ color: '#6d28d9' }}>
              Текущий комплект ожидает решения контроля качества
              {quality.round > 1 ? ` · круг ${quality.round}` : ''}
            </Typography>
          </Box>
          {chatId && (
            <Button size="sm" variant="plain" color="primary"
              startDecorator={<UnreadChatBadge chatId={chatId}><ChatBubbleRoundedIcon sx={{ fontSize: 15 }} /></UnreadChatBadge>}
              onClick={() => onChat?.(chatId)}>
              Чат
            </Button>
          )}
        </Stack>
      </Sheet>
    );
  }

  if (state === 'rework_accepted') {
    return (
      <Sheet variant="soft" sx={{
        p: 1.25, borderRadius: '12px', mb: 1.25,
        bgcolor: '#fffbeb', border: '1px solid #fde68a',
      }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <BuildRoundedIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography level="body-sm" sx={{ fontWeight: 700, color: '#92400e' }}>
              Доработка в работе
            </Typography>
            <Typography level="body-xs" sx={{ color: '#b45309' }}>
              Загрузите исправленные чертежи в блоке файлов события,
              затем нажмите «Завершить доработку».
            </Typography>
          </Box>
          {chatId && (
            <Button size="sm" variant="plain" color="warning"
              startDecorator={<UnreadChatBadge chatId={chatId}><ChatBubbleRoundedIcon sx={{ fontSize: 15 }} /></UnreadChatBadge>}
              onClick={() => onChat?.(chatId)}>
              Чат и файлы
            </Button>
          )}
        </Stack>

        {last?.comment && (
          <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed #fde68a' }}>
            <Typography level="body-xs" sx={{ color: '#92400e', whiteSpace: 'pre-line' }}>
              {last.comment}
            </Typography>
            {!!(last.files || []).length && <FileStrip files={last.files} />}
          </Box>
        )}
      </Sheet>
    );
  }

  // ── rework: главное состояние, его нельзя пропустить ──
  return (
    <Sheet
      component={motion.div}
      // Пульсация тенью, а не размером: карточка стоит в сетке, и скачущие
      // габариты дёргали бы соседей.
      animate={{
        boxShadow: [
          '0 0 0 0 rgba(220,38,38,0.45)',
          '0 0 0 10px rgba(220,38,38,0)',
          '0 0 0 0 rgba(220,38,38,0)',
        ],
      }}
      transition={{ duration: 1.9, repeat: Infinity, ease: 'easeOut' }}
      variant="soft"
      sx={{
        p: 1.5, borderRadius: '12px', mb: 1.25,
        bgcolor: '#fef2f2', border: '1.5px solid #fca5a5',
      }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
        <Box component={motion.div}
          animate={{ rotate: [0, -14, 14, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, repeatDelay: 1.2 }}
          sx={{ lineHeight: 0, mt: 0.25 }}
        >
          <ReplayRoundedIcon sx={{ fontSize: 20, color: '#dc2626' }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography level="body-sm" sx={{ fontWeight: 800, color: '#991b1b' }}>
              Контроль вернул на доработку
            </Typography>
            {last?.round > 0 && (
              <Chip size="sm" variant="soft" color="danger"
                sx={{ '--Chip-minHeight': '18px', fontSize: '0.62rem' }}>
                круг {last.round}
              </Chip>
            )}
          </Stack>
          <Typography level="body-xs" sx={{ color: '#b91c1c', mt: 0.25 }}>
            {last?.reviewer || 'Мастер по качеству'}
            {last?.created ? ` · ${fmtDateTime(last.created)}` : ''}
          </Typography>
        </Box>
        <Button size="sm" variant="plain" color="danger"
          onClick={() => setOpen((v) => !v)}
          sx={{ minHeight: 24, px: 0.75 }}>
          {open ? 'Свернуть' : 'Что не так'}
        </Button>
      </Stack>

      <AnimatePresence initial={false}>
        {open && last && (
          <Box component={motion.div}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            sx={{ overflow: 'hidden' }}
          >
            <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed #fca5a5' }}>
              {last.stars > 0 && (
                <Box sx={{ mb: 0.5 }}>
                  <StarPicker value={last.stars} size={15} readOnly />
                </Box>
              )}
              <Typography level="body-sm" sx={{ whiteSpace: 'pre-line', color: '#7f1d1d' }}>
                {last.comment}
              </Typography>
              {!!(last.files || []).length && <FileStrip files={last.files} />}
            </Box>
          </Box>
        )}
      </AnimatePresence>

      <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: 'wrap', rowGap: 1 }}>
        <Button
          component={motion.button}
          // Кнопка тоже дышит: пока её не нажали, работа стоит, и это
          // должно быть заметно с другого конца комнаты.
          animate={{ scale: [1, 1.035, 1] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          size="sm" color="danger" variant="solid" loading={busy} disabled={!onAccept}
          startDecorator={!busy ? <PlayArrowRoundedIcon /> : null}
          onClick={async () => { if (busy) return; setBusy(true); try { await onAccept?.(); } finally { setBusy(false); } }}
          sx={{ flex: '1 1 auto' }}
        >
          Принять на доработку
        </Button>
        {chatId && (
          <Button size="sm" variant="soft" color="danger"
            startDecorator={<UnreadChatBadge chatId={chatId}><ChatBubbleRoundedIcon sx={{ fontSize: 15 }} /></UnreadChatBadge>}
            onClick={() => onChat?.(chatId)}>
            Обсудить
          </Button>
        )}
      </Stack>
    </Sheet>
  );
}
